import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Divider,
  LinearProgress, Step, StepLabel, Stepper, Tab, Tabs,
  TextField, Typography, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SendIcon from "@mui/icons-material/Send";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApartmentIcon from "@mui/icons-material/Apartment";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import PageviewIcon from "@mui/icons-material/Pageview";
import BuildIcon from "@mui/icons-material/Build";
import DoneIcon from "@mui/icons-material/Done";

// ── Design tokens (matching design_handoff_support_desk) ──────────────────────
const T = {
  navy: "#0A1E3D",
  orange: "#F16522",
  orangeGrad: "linear-gradient(100deg,#F16522 0%,#F7941E 100%)",
  bg: "#EEF2F8",
  card: "#fff",
  ink: "#101B30",
  ink2: "#4A5A76",
  ink3: "#8494AD",
  line: "#E3E9F2",
  accentSoft: "#FDEDE2",
};

const STATUS_MAP = {
  submitted: { label: "Submitted",       color: "#2B5BC4", bg: "#EAF0FB" },
  review:    { label: "Under review",    color: "#B7791F", bg: "#FEF3E0" },
  district:  { label: "Sent to district",color: "#6B4EE0", bg: "#EEEAFB" },
  progress:  { label: "In progress",     color: "#1673C4", bg: "#E4F2FF" },
  resolved:  { label: "Resolved",        color: "#12885A", bg: "#E3F5EC" },
  reopened:  { label: "Reopened",        color: "#CC3A2E", bg: "#FDE9E7" },
};

const STATUS_BANNER = {
  submitted: { headline: "We've received your request",           sub: "It's in the queue. A support officer will pick it up shortly.",                                                            bg: "#EAF0FB", color: "#2B5BC4", Icon: MoveToInboxIcon   },
  review:    { headline: "Support is reviewing your request",     sub: "An officer is verifying the details before taking the next step.",                                                          bg: "#FEF3E0", color: "#B7791F", Icon: PageviewIcon       },
  district:  { headline: "Sent to your district for action",      sub: "Your district education officer needs to act. We'll update you as soon as they respond.",                                   bg: "#EEEAFB", color: "#6B4EE0", Icon: ApartmentIcon      },
  progress:  { headline: "Work is in progress",                   sub: "The team is actively working on a fix. Hang tight.",                                                                       bg: "#E4F2FF", color: "#1673C4", Icon: BuildIcon           },
  resolved:  { headline: "Your request has been resolved",        sub: "See the actions taken below. You can reopen it within 14 days if needed.",                                                 bg: "#E3F5EC", color: "#12885A", Icon: CheckCircleIcon     },
  reopened:  { headline: "Request reopened — we're looking again",sub: "Thanks for the extra detail — support has this again.",                                                                     bg: "#FDE9E7", color: "#CC3A2E", Icon: RefreshIcon         },
};

// Fallback list used only if the configurable categories fail to load.
const DEFAULT_CATEGORIES = [
  { key: "account",   name: "Login & account access",        desc: "Can't sign in, locked out, password or National ID mismatch.", color: "#2563EB" },
  { key: "transfer",    name: "Transfer",                    desc: "Transfer request stuck or wrong school.",                      color: "#6B4EE0" },
  { key: "permutation", name: "Permutation",                 desc: "Permutation request stuck or pending.",                        color: "#8B5CF6" },
  { key: "data",      name: "Data correction",               desc: "Wrong name, National ID, school or qualification on record.",  color: "#E8912B" },
  { key: "maternity",   name: "Maternity leave", desc: "Maternity leave not reflecting, status or dates.", color: "#12885A" },
  { key: "scholarship", name: "Scholarship",     desc: "Scholarship status, eligibility or disbursement.", color: "#0891B2" },
  { key: "cpd",         name: "CPD",             desc: "CPD points, courses or certification.",            color: "#9333EA" },
  { key: "placement", name: "Placement & deployment",        desc: "Not deployed, wrong placement, posting not visible.",         color: "#1673C4" },
  { key: "bug",       name: "Technical bug / system error",  desc: "Page error, upload fails, something is broken.",              color: "#E0453A" },
  { key: "other",     name: "Other / general enquiry",       desc: "Anything else — a question or general request.",              color: "#5B6B85" },
];

// Resolve a category (incl. inactive ones) for display, with a safe fallback.
const catByKey = (cats, key) => {
  const list = (cats && cats.length) ? cats : DEFAULT_CATEGORIES;
  return list.find(c => c.key === key) || list.find(c => c.key === "other") || list[list.length - 1];
};

const PRIORITIES = [
  { key: "high", label: "High",   color: "#E0453A", desc: "Urgent — blocking my work or deadline" },
  { key: "med",  label: "Medium", color: "#E8912B", desc: "Important but not immediately blocking" },
  { key: "low",  label: "Low",    color: "#3E9C6B", desc: "When you get a chance" },
];

const FLOW = ["submitted", "review", "district", "progress", "resolved"];

// ── Small shared components ───────────────────────────────────────────────────

function StatusPill({ status }) {
  const s = STATUS_MAP[status] || { label: status, color: T.ink2, bg: T.bg };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color,
      borderRadius: 999, padding: "3px 10px",
      fontWeight: 700, fontSize: 12, letterSpacing: ".02em",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, display: "inline-block", flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

function TimeAgo({ iso }) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function SdCard({ children, style }) {
  return (
    <Box sx={{
      background: T.card, borderRadius: 3,
      boxShadow: "0 6px 18px -8px rgba(16,27,48,.18),0 2px 6px rgba(16,27,48,.05)",
      p: 3, ...style,
    }}>
      {children}
    </Box>
  );
}

function SdBtn({ children, variant = "primary", onClick, disabled, fullWidth, size = "md", startIcon, endIcon }) {
  const isPrimary = variant === "primary";
  const isOutline = variant === "outline";
  const isSoft    = variant === "soft";
  const bg = isPrimary ? T.orangeGrad : isSoft ? T.accentSoft : "transparent";
  const color = isPrimary ? "#fff" : T.orange;
  const border = isOutline ? `1.5px solid ${T.orange}` : "none";
  const pad = size === "lg" ? "12px 28px" : "8px 20px";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: disabled ? "#e5e7eb" : bg,
        color: disabled ? "#9ca3af" : color,
        border, borderRadius: 999, padding: pad,
        fontWeight: 700, fontSize: size === "lg" ? 15 : 14,
        cursor: disabled ? "not-allowed" : "pointer",
        width: fullWidth ? "100%" : undefined,
        justifyContent: "center",
        transition: "opacity .15s",
      }}
    >
      {startIcon && <span style={{ display: "flex" }}>{startIcon}</span>}
      {children}
      {endIcon && <span style={{ display: "flex" }}>{endIcon}</span>}
    </button>
  );
}

// ── Screen: TeacherEntry ──────────────────────────────────────────────────────

function TeacherEntry({ onVerified }) {
  const [nid, setNid]     = useState("");
  const [phase, setPhase] = useState("input"); // input | checking | otp | verifying | found
  const [citizen, setCitizen] = useState(null);
  const [err, setErr]     = useState("");
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneHint, setPhoneHint] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [otpReused, setOtpReused] = useState(false);
  const [otpNotice, setOtpNotice] = useState("");
  const [accessToken, setAccessToken] = useState(null);

  const digits = nid.replace(/\D/g, "");
  const valid  = digits.length === 16;

  // Learn whether OTP gating is on (defaults off → unchanged flow).
  useEffect(() => {
    axios.get("/api/support-desk/settings/public")
      .then(({ data }) => setOtpRequired(!!data.otpRequired))
      .catch(() => {});
  }, []);

  const fmtNid = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 16);
    const parts = [];
    if (d.length > 0)  parts.push(d.slice(0, 1));
    if (d.length > 1)  parts.push(d.slice(1, 5));
    if (d.length > 5)  parts.push(d.slice(5, 6));
    if (d.length > 6)  parts.push(d.slice(6, 13));
    if (d.length > 13) parts.push(d.slice(13, 14));
    if (d.length > 14) parts.push(d.slice(14, 16));
    return parts.join(" ");
  };

  const verify = async () => {
    if (!valid) { setErr("Enter your full 16-digit National ID."); return; }
    setErr(""); setPhase("checking");
    try {
      if (otpRequired) {
        // Do NOT fetch details yet — only send/reuse a code to the registered phone.
        const { data } = await axios.post("/api/support-desk/tickets/send-otp", { nid: digits });
        setPhoneHint(data.phoneHint || "");
        setEmailHint(data.emailHint || "");
        setOtpReused(!!data.reused);
        setOtpNotice("");
        setOtpCode("");
        setPhase("otp");
      } else {
        const { data } = await axios.post("/api/support-desk/tickets/verify-nid", { nid: digits });
        setCitizen(data);
        setPhase("found");
      }
    } catch (e) {
      setErr(e?.response?.data || "We could not verify this National ID. Please check and try again.");
      setPhase("input");
    }
  };

  const verifyOtp = async () => {
    const code = otpCode.replace(/\D/g, "");
    if (code.length < 4) { setErr("Enter the code sent to your phone."); return; }
    setErr(""); setPhase("verifying");
    try {
      const { data } = await axios.post("/api/support-desk/tickets/verify-otp", { nid: digits, code });
      setCitizen(data.citizen);
      setAccessToken(data.accessToken);
      setPhase("found");
    } catch (e) {
      setErr(e?.response?.data || "Incorrect or expired code. Please try again.");
      setPhase("otp");
    }
  };

  // "Send a new code" forces a fresh SMS (use only if the teacher lost their code).
  const resendOtp = async () => {
    setErr(""); setOtpNotice("");
    try {
      const { data } = await axios.post("/api/support-desk/tickets/send-otp", { nid: digits, force: true });
      setPhoneHint(data.phoneHint || "");
      setEmailHint(data.emailHint || "");
      setOtpReused(false);
      setOtpNotice("A new code has been sent to your phone and email. It replaces any previous code.");
    } catch (e) {
      setErr(e?.response?.data || "Could not send a new code.");
    }
  };

  // Normalize IPPS response — API returns firstName/lastName at root, school/district under employee
  const normalize = (c) => {
    if (!c) return {};
    const emp = c.employee || {};
    const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ")
      || c.names || c.name || "";
    return {
      fullName,
      initials: fullName.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase(),
      code:     emp.employeeCode || emp.staffCode || c.employeeCode || c.staffCode || c.teacherCode || "",
      role:     emp.postTitle || emp.positionTitle || emp.position || c.postTitle || "",
      school:   emp.schoolName || c.schoolName || c.school || "",
      district: emp.districtName || c.districtName || c.district || "",
      sector:   emp.sectorName || c.sectorName || c.sector || "",
      phone:    c.phoneNumber || emp.phoneNumber || c.phone || "",
    };
  };

  const initials = (name = "") => name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Box sx={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
      {/* Top bar */}
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, background: T.navy, px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, zIndex: 10 }}>
        <HelpOutlineIcon sx={{ color: T.orange, fontSize: 22 }} />
        <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>TMIS <em style={{ color: T.orange }}>Support</em></Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Link to="/" style={{ color: "#aab4c8", fontSize: 13, textDecoration: "none" }}>← Back to TMIS</Link>
      </Box>

      <Box sx={{ mt: 8, maxWidth: 480, width: "100%" }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box sx={{ width: 58, height: 58, borderRadius: "17px", background: T.orangeGrad, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <HelpOutlineIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Typography variant="overline" sx={{ color: T.orange, fontWeight: 800, letterSpacing: ".09em" }}>TMIS Support Desk</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: T.navy, mt: 1 }}>How can we help you today?</Typography>
          <Typography sx={{ color: T.ink2, mt: 1, fontSize: 14 }}>
            Enter your National ID to get started. We'll verify your account with IPPS so your details are ready.
          </Typography>
        </Box>

        <SdCard>
          {(phase === "input" || phase === "checking") && (
            <>
              {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
              <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>National ID <span style={{ color: T.orange }}>*</span></Typography>
              <Typography sx={{ fontSize: 12, color: T.ink3, mb: 1 }}>16 digits, exactly as on your Indangamuntu.</Typography>
              <input
                type="text"
                inputMode="numeric"
                placeholder="1 1990 8 0000000 0 00"
                value={nid}
                disabled={phase === "checking"}
                onChange={(e) => { setNid(fmtNid(e.target.value)); setErr(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
                style={{
                  width: "100%", padding: "12px 16px", fontSize: 18,
                  letterSpacing: ".12em", border: `1.5px solid ${err ? "#ef4444" : T.line}`,
                  borderRadius: 12, outline: "none", fontFamily: "monospace",
                  boxSizing: "border-box", background: "#f9fafb",
                }}
              />
              <Box sx={{ mt: 2 }}>
                <SdBtn
                  fullWidth size="lg"
                  disabled={phase === "checking"}
                  onClick={verify}
                >
                  {phase === "checking" ? <><CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} /> Verifying with IPPS…</> : "Verify my account"}
                </SdBtn>
              </Box>
              <Typography sx={{ textAlign: "center", fontSize: 12, color: T.ink3, mt: 1.5 }}>
                Your ID is checked securely against the national payroll (IPPS).
              </Typography>
            </>
          )}

          {(phase === "otp" || phase === "verifying") && (
            <>
              {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
              {otpNotice && <Alert severity="success" sx={{ mb: 2 }}>{otpNotice}</Alert>}
              <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>Enter your code</Typography>
              <Typography sx={{ fontSize: 13, color: T.ink2, mb: 1.5 }}>
                {otpReused ? (
                  <>Enter the <b>same code</b> we already sent you{(phoneHint || emailHint) ? <> (to {phoneHint ? <>SMS <b>{phoneHint}</b></> : null}{phoneHint && emailHint ? " and " : null}{emailHint ? <>email <b>{emailHint}</b></> : null})</> : ""}. To save cost, we did <b>not</b> send a new one.</>
                ) : (
                  <>We sent a 6-digit code to your registered {phoneHint ? <>phone <b>{phoneHint}</b></> : null}{phoneHint && emailHint ? " and " : null}{emailHint ? <>email <b>{emailHint}</b></> : null}. Enter it below to continue.</>
                )}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", background: T.accentSoft, borderRadius: 2, p: 1.25, mb: 2 }}>
                <HelpOutlineIcon sx={{ fontSize: 16, color: T.orange, mt: "1px", flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12, color: T.ink2 }}>
                  Keep this code — it stays the same. <b>Reuse it every time</b> to check your requests; we won't SMS a new one.
                </Typography>
              </Box>
              <input
                type="text"
                inputMode="numeric"
                placeholder="______"
                value={otpCode}
                disabled={phase === "verifying"}
                onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setErr(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(); }}
                style={{
                  width: "100%", padding: "12px 16px", fontSize: 22, textAlign: "center",
                  letterSpacing: ".4em", border: `1.5px solid ${err ? "#ef4444" : T.line}`,
                  borderRadius: 12, outline: "none", fontFamily: "monospace",
                  boxSizing: "border-box", background: "#f9fafb",
                }}
              />
              <Box sx={{ mt: 2 }}>
                <SdBtn fullWidth size="lg" disabled={phase === "verifying"} onClick={verifyOtp}>
                  {phase === "verifying" ? <><CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} /> Verifying…</> : "Verify code"}
                </SdBtn>
              </Box>
              <Typography sx={{ textAlign: "center", fontSize: 12, color: T.ink3, mt: 1.5 }}>
                Lost your code?{" "}
                <span style={{ color: T.orange, fontWeight: 700, cursor: "pointer" }} onClick={resendOtp}>Send a new code</span>
                {"  ·  "}
                <span style={{ color: T.orange, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => { setPhase("input"); setOtpCode(""); setErr(""); setOtpNotice(""); }}>Change ID</span>
              </Typography>
            </>
          )}

          {phase === "found" && citizen && (() => {
            const p = normalize(citizen);
            return (
            <Box>
              <Chip label="Account verified in IPPS" color="success" size="small" icon={<CheckCircleIcon />} sx={{ mb: 2, fontWeight: 700 }} />
              {/* Identity row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                <Box sx={{ width: 54, height: 54, borderRadius: "50%", background: "linear-gradient(135deg,#2563eb,#1e3a8a)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 20, flexShrink: 0 }}>
                  {p.initials || "?"}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 17, color: T.ink }}>{p.fullName || "—"}</Typography>
                  <Typography sx={{ fontSize: 13, color: T.ink2 }}>
                    {[p.code, p.role].filter(Boolean).join(" · ") || "Teacher"}
                  </Typography>
                </Box>
              </Box>
              {/* Info rows with icons + labels */}
              {[
                { icon: <ApartmentIcon sx={{ fontSize: 17, color: T.ink3 }} />, label: "SCHOOL",           val: p.school },
                { icon: <LocationOnIcon sx={{ fontSize: 17, color: T.ink3 }} />, label: "DISTRICT / SECTOR", val: [p.district, p.sector].filter(Boolean).join(" · ") },
                { icon: <PhoneIcon sx={{ fontSize: 17, color: T.ink3 }} />,      label: "CONTACT",          val: p.phone },
              ].filter(r => r.val).map((r, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 1.5, borderTop: `1px solid ${T.line}` }}>
                  <Box sx={{ pt: "2px", flexShrink: 0 }}>{r.icon}</Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.ink3, letterSpacing: ".08em", mb: .25 }}>{r.label}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{r.val}</Typography>
                  </Box>
                </Box>
              ))}
              <Box sx={{ mt: 2 }}>
                <SdBtn fullWidth size="lg" onClick={() => onVerified({ ...citizen, ...p, names: p.fullName, nid: digits }, accessToken)} endIcon={<ArrowForwardIcon />}>
                  Continue to my Support Desk
                </SdBtn>
              </Box>
              <Typography sx={{ textAlign: "center", mt: 1.5, fontSize: 12, color: T.ink3 }}>
                Not you?{" "}
                <span style={{ color: T.orange, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => { setPhase("input"); setNid(""); setCitizen(null); }}>
                  Use a different ID
                </span>
              </Typography>
            </Box>
            );
          })()}
        </SdCard>
      </Box>
    </Box>
  );
}

// ── Screen: TeacherHome ───────────────────────────────────────────────────────

function TeacherHome({ teacher, tickets, onNew, onOpen, loading, categories }) {
  const [tab, setTab] = useState(0);
  const open     = tickets.filter(t => !["resolved"].includes(t.status));
  const resolved = tickets.filter(t => t.status === "resolved");
  const lists    = [open, resolved, tickets];

  return (
    <Box sx={{ minHeight: "100vh", background: T.bg }}>
      <TopBar teacher={teacher} />
      <Box sx={{ maxWidth: 740, mx: "auto", px: 2, pt: 3, pb: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="overline" sx={{ color: T.orange, fontWeight: 800 }}>Welcome back</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: T.navy, mt: .5 }}>Hello, {teacher.firstName || teacher.names?.split(" ")[0]} 👋</Typography>
            <Typography sx={{ color: T.ink2, fontSize: 14 }}>Track your support requests or raise a new one — we're here to help.</Typography>
          </Box>
          <SdBtn size="lg" onClick={onNew}>+ New support request</SdBtn>
        </Box>

        {/* KPI row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2, mb: 3 }}>
          {[
            { label: "Active requests",  n: open.length,     bg: "#E4F2FF", color: "#1673C4" },
            { label: "Awaiting district",n: tickets.filter(t=>t.status==="district").length, bg: "#EEEAFB", color: "#6B4EE0" },
            { label: "Resolved",         n: resolved.length, bg: "#E3F5EC", color: "#12885A" },
          ].map(k => (
            <SdCard key={k.label} style={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: k.color }}>{k.n}</Typography>
              <Typography sx={{ fontSize: 13, color: T.ink2 }}>{k.label}</Typography>
            </SdCard>
          ))}
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          {["Active", "Resolved", "All"].map((l, i) => (
            <Tab key={l} label={`${l} (${[open,resolved,tickets][i].length})`} sx={{ fontWeight: 700, fontSize: 13 }} />
          ))}
        </Tabs>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {lists[tab].length === 0 ? (
          <SdCard style={{ textAlign: "center", py: 5 }}>
            <Typography sx={{ color: T.ink2, mb: 2 }}>No requests here yet.</Typography>
            <SdBtn onClick={onNew}>Raise your first request</SdBtn>
          </SdCard>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {lists[tab].map(t => <TicketRow key={t.id} ticket={t} onOpen={() => onOpen(t)} categories={categories} />)}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TicketRow({ ticket: t, onOpen, categories }) {
  const cat = catByKey(categories, t.categoryKey);
  const last = t.timeline && t.timeline[t.timeline.length - 1];
  return (
    <Box
      onClick={onOpen}
      sx={{
        display: "grid", gridTemplateColumns: "auto 1fr auto",
        alignItems: "center", gap: 2,
        background: T.card, borderRadius: 2.5,
        boxShadow: "0 2px 8px rgba(16,27,48,.06)",
        p: 2, cursor: "pointer",
        "&:hover": { boxShadow: "0 6px 18px rgba(16,27,48,.12)", transform: "translateY(-1px)" },
        transition: "all .2s",
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: cat.color + "22", color: cat.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
        {cat.name.slice(0, 2).toUpperCase()}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {t.subject}
        </Typography>
        <Typography sx={{ fontSize: 12, color: T.ink3, fontFamily: "monospace" }}>{t.ref}</Typography>
        <Typography sx={{ fontSize: 12, color: T.ink2 }}>
          {cat.name} · {last ? <TimeAgo iso={last.createdAt} /> : ""}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: .5, flexShrink: 0 }}>
        <StatusPill status={t.status} />
      </Box>
    </Box>
  );
}

// ── Screen: NewRequest (4-step wizard) ────────────────────────────────────────

function NewRequest({ teacher, onCancel, onSubmit, categories, authHeaders = {} }) {
  const activeCategories = ((categories && categories.length) ? categories : DEFAULT_CATEGORIES)
    .filter(c => c.isActive !== false);
  const [step, setStep]       = useState(0);
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("med");
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [files, setFiles]     = useState([]);
  const [districts, setDistricts] = useState([]);
  const [district, setDistrict]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errs, setErrs]       = useState({});
  const fileRef = useRef();

  const steps = ["Category", "Details", "Attachments", "Review"];

  // Load the canonical district list and pre-select the teacher's IPPS district when it matches.
  useEffect(() => {
    let active = true;
    axios.get("/api/support-desk/districts")
      .then(({ data }) => {
        if (!active) return;
        setDistricts(data || []);
        const ippsName = (teacher?.districtName || teacher?.district || "").trim().toLowerCase();
        if (ippsName) {
          const match = (data || []).find(d => (d.name || "").trim().toLowerCase() === ippsName);
          if (match) setDistrict(match);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [teacher]);

  const validate = () => {
    const e = {};
    if (step === 0 && !category) e.category = "Please select a category.";
    if (step === 1) {
      const selCat = activeCategories.find(c => c.key === category);
      const opts = (selCat && selCat.subjectOptions) || [];
      if (opts.length) {
        if (!subject) e.subject = "Please select a request type.";
      } else if (subject.length < 6) {
        e.subject = "Subject must be at least 6 characters.";
      }
      if (body.length < 20)    e.body    = "Description must be at least 20 characters.";
      if (!district)           e.district = "Please select your district.";
    }
    setErrs(e);
    return !Object.keys(e).length;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const prev = () => setStep(s => s - 1);

  const handleFiles = (e) => {
    const added = Array.from(e.target.files || []);
    setFiles(f => [...f, ...added].slice(0, 5));
    e.target.value = "";
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const form = new FormData();
    form.append("categoryKey",      category);
    form.append("priorityKey",      priority);
    form.append("subject",          subject);
    form.append("body",             body);
    form.append("teacherNid",       (teacher.nationalId || teacher.nid || "").replace(/\s/g, ""));
    form.append("teacherName",      teacher.names || teacher.name || "");
    form.append("teacherCode",      teacher.employeeCode || teacher.staffCode || "");
    form.append("teacherSchool",    teacher.schoolName || teacher.school || "");
    form.append("teacherDistrict",  district?.name || teacher.districtName || teacher.district || "");
    form.append("teacherSector",    teacher.sectorName || teacher.sector || "");
    form.append("teacherPhone",     teacher.phone || teacher.phoneNumber || "");
    form.append("teacherInitials",  (teacher.names || "").split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase());
    files.forEach(f => form.append("attachments", f));
    try {
      const { data } = await axios.post("/api/support-desk/tickets", form, {
        headers: { "Content-Type": "multipart/form-data", ...authHeaders },
      });
      onSubmit(data);
    } catch (e) {
      setErrs({ submit: e?.response?.data || "Submission failed. Please try again." });
      setSubmitting(false);
    }
  };

  const cat = activeCategories.find(c => c.key === category);

  return (
    <Box sx={{ minHeight: "100vh", background: T.bg }}>
      <TopBar teacher={teacher} />
      <Box sx={{ maxWidth: 740, mx: "auto", px: 2, pt: 3, pb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SdBtn variant="soft" onClick={onCancel} startIcon={<ArrowBackIcon fontSize="small" />}>Back</SdBtn>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: T.navy, ml: 1 }}>New support request</Typography>
        </Box>

        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{ mb: 4, "& .MuiStepLabel-label": { fontSize: { xs: 10, sm: 13 }, mt: { xs: .5, sm: 1 } } }}
        >
          {steps.map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        <SdCard>
          {/* Step 0: Category */}
          {step === 0 && (
            <>
              <Typography sx={{ fontWeight: 700, mb: 2, color: T.ink }}>What's your request about?</Typography>
              {errs.category && <Alert severity="error" sx={{ mb: 2 }}>{errs.category}</Alert>}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 1.5 }}>
                {activeCategories.map(c => (
                  <Box
                    key={c.key}
                    onClick={() => { setCategory(c.key); setSubject(""); setErrs({}); }}
                    sx={{
                      border: `2px solid ${category === c.key ? c.color : T.line}`,
                      borderRadius: 2.5, p: 2, cursor: "pointer",
                      background: category === c.key ? c.color + "11" : T.card,
                      transition: "all .15s", position: "relative",
                    }}
                  >
                    {category === c.key && (
                      <Box sx={{ position: "absolute", top: 10, right: 10, width: 20, height: 20, borderRadius: "50%", background: c.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <DoneIcon sx={{ color: "#fff", fontSize: 13 }} />
                      </Box>
                    )}
                    <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: c.color + "22", color: c.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, mb: 1, fontSize: 13 }}>
                      {c.name.slice(0, 2).toUpperCase()}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.ink }}>{c.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: T.ink3, mt: .5 }}>{c.desc}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Step 1: Details */}
          {step === 1 && (
            <>
              <Typography sx={{ fontWeight: 700, mb: 2, color: T.ink }}>Tell us more</Typography>
              {cat && cat.subjectOptions && cat.subjectOptions.length ? (
                <Autocomplete
                  value={subject || null}
                  options={cat.subjectOptions}
                  onChange={(e, v) => { setSubject(v || ""); setErrs(er => ({ ...er, subject: "" })); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Request type" required
                      error={!!errs.subject}
                      helperText={errs.subject || "Choose the type of request"}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
              ) : (
                <TextField
                  label="Subject" fullWidth required
                  error={!!errs.subject} helperText={errs.subject || "Min 6 characters"}
                  value={subject} onChange={e => { setSubject(e.target.value); setErrs(v => ({...v, subject: ""})); }}
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                label="Description" fullWidth required multiline rows={5}
                error={!!errs.body} helperText={errs.body || "Min 20 characters — describe the issue in detail"}
                value={body} onChange={e => { setBody(e.target.value); setErrs(v => ({...v, body: ""})); }}
                sx={{ mb: 2 }}
              />
              <Autocomplete
                value={district}
                options={districts}
                getOptionLabel={(o) => o.name || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(e, v) => { setDistrict(v || null); setErrs(er => ({ ...er, district: "" })); }}
                renderOption={(props, o) => <Box component="li" {...props} key={o.id}>{o.name}</Box>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="District" required
                    error={!!errs.district}
                    helperText={errs.district || "Which district does this request relate to?"}
                  />
                )}
                sx={{ mb: 2 }}
              />
              <Typography sx={{ fontWeight: 700, mb: 1, fontSize: 14 }}>Priority</Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {PRIORITIES.map(p => (
                  <Box
                    key={p.key}
                    onClick={() => setPriority(p.key)}
                    sx={{
                      border: `2px solid ${priority === p.key ? p.color : T.line}`,
                      borderRadius: 2, p: 1.5, cursor: "pointer", flex: "1 1 140px",
                      background: priority === p.key ? p.color + "11" : T.card,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: p.color }}>{p.label}</Typography>
                    <Typography sx={{ fontSize: 12, color: T.ink3 }}>{p.desc}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Step 2: Attachments */}
          {step === 2 && (
            <>
              <Typography sx={{ fontWeight: 700, mb: 1, color: T.ink }}>Attachments <span style={{ color: T.ink3, fontWeight: 400 }}>(optional)</span></Typography>
              <Typography sx={{ fontSize: 13, color: T.ink2, mb: 2 }}>Upload supporting documents — PDF, images, Word or Excel files (max 5 MB each, up to 5 files).</Typography>
              <Box
                onClick={() => fileRef.current?.click()}
                sx={{
                  border: `2px dashed ${T.line}`, borderRadius: 2.5, p: 4,
                  textAlign: "center", cursor: "pointer",
                  "&:hover": { borderColor: T.orange, background: T.accentSoft },
                  transition: "all .15s", mb: 2,
                }}
              >
                <AttachFileIcon sx={{ color: T.ink3, mb: 1 }} />
                <Typography sx={{ color: T.ink2, fontSize: 14 }}>Click to browse files</Typography>
              </Box>
              <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx" style={{ display: "none" }} onChange={handleFiles} />
              {files.map((f, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, background: "#f9fafb", borderRadius: 1.5, mb: 1 }}>
                  <AttachFileIcon sx={{ color: T.orange, fontSize: 18 }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: T.ink3 }}>{(f.size / 1024).toFixed(0)} KB</Typography>
                  </Box>
                  <button onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontWeight: 700 }}>×</button>
                </Box>
              ))}
            </>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <>
              <Typography sx={{ fontWeight: 700, mb: 2, color: T.ink }}>Review your request</Typography>
              {errs.submit && <Alert severity="error" sx={{ mb: 2 }}>{errs.submit}</Alert>}
              {[
                ["Subject",     subject],
                ["Category",    cat?.name],
                ["Priority",    PRIORITIES.find(p => p.key === priority)?.label],
                ["Description", body],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: "flex", gap: 2, mb: 1.5, pb: 1.5, borderBottom: `1px solid ${T.line}` }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.ink2, minWidth: 100 }}>{k}</Typography>
                  <Typography sx={{ fontSize: 13, color: T.ink }}>{v}</Typography>
                </Box>
              ))}
              {files.length > 0 && (
                <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.ink2, minWidth: 100 }}>Attachments</Typography>
                  <Box>{files.map((f, i) => <Typography key={i} sx={{ fontSize: 13 }}>{f.name}</Typography>)}</Box>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: T.ink2, minWidth: 100 }}>Submitting as</Typography>
                <Typography sx={{ fontSize: 13 }}>{teacher.names || teacher.name}</Typography>
              </Box>
            </>
          )}

          {/* Nav buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <SdBtn variant="outline" onClick={step === 0 ? onCancel : prev} startIcon={<ArrowBackIcon fontSize="small" />}>
              {step === 0 ? "Cancel" : "Back"}
            </SdBtn>
            {step < 3 ? (
              <SdBtn onClick={next} endIcon={<ArrowForwardIcon fontSize="small" />}>Continue</SdBtn>
            ) : (
              <SdBtn onClick={handleSubmit} disabled={submitting}>
                {submitting ? <><CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> Submitting…</> : "Submit request"}
              </SdBtn>
            )}
          </Box>
        </SdCard>
      </Box>
    </Box>
  );
}

// ── Screen: Confirmation ──────────────────────────────────────────────────────

function Confirmation({ ticket, teacher, onHome, onTrack }) {
  return (
    <Box sx={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 2 }}>
      <TopBar teacher={teacher} />
      <Box sx={{ maxWidth: 480, width: "100%", textAlign: "center", mt: 8 }}>
        <style>{`
          @keyframes sd-pop{0%{transform:scale(.3);opacity:0}70%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}
          @keyframes sd-ring{0%{transform:scale(.9);opacity:.7}100%{transform:scale(1.9);opacity:0}}
          .sd-pop{animation:sd-pop .5s cubic-bezier(.34,1.56,.64,1) both}
          .sd-ring{animation:sd-ring 1.3s ease-out .45s infinite}
        `}</style>
        <Box sx={{ position: "relative", width: 72, height: 72, mx: "auto", mb: 3 }}>
          <Box className="sd-ring" sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2.5px solid #12885A", pointerEvents: "none" }} />
          <Box className="sd-pop" sx={{ width: 72, height: 72, borderRadius: "50%", background: "#E3F5EC", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckCircleIcon sx={{ color: "#12885A", fontSize: 40 }} />
          </Box>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: T.navy, mb: 1 }}>Request submitted!</Typography>
        <Typography sx={{ color: T.ink2, mb: 2 }}>
          Your support request has been received. You'll receive updates as it progresses.
        </Typography>
        <Box sx={{ background: T.navy, color: "#fff", borderRadius: 2, px: 3, py: 1.5, display: "inline-block", fontFamily: "monospace", fontWeight: 700, fontSize: 16, mb: 3 }}>
          {ticket.ref}
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <SdBtn variant="outline" onClick={onHome}>My requests</SdBtn>
          <SdBtn onClick={onTrack}>Track this request</SdBtn>
        </Box>
      </Box>
    </Box>
  );
}

// ── Screen: TrackDetail ───────────────────────────────────────────────────────

function TrackDetail({ ticket: initialTicket, teacher, onBack, categories, authHeaders = {} }) {
  const [ticket, setTicket] = useState(initialTicket);
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending]     = useState(false);
  const [reopening, setReopening] = useState(false);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const replyFileRef = useRef();

  const refresh = async () => {
    setLoadingTicket(true);
    try {
      const { data } = await axios.get(`/api/support-desk/tickets/by-ref/${ticket.ref}`, { headers: authHeaders });
      setTicket(data);
    } catch {}
    setLoadingTicket(false);
  };

  // Always load full ticket (with comments) on mount — the list only carries metadata
  useEffect(() => { refresh(); }, []);

  const sendReply = async () => {
    if (!replyText.trim() && !replyFiles.length) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("body", replyText);
      form.append("teacherNid", (teacher.nationalId || teacher.nid || "").replace(/\s/g, ""));
      form.append("teacherName", teacher.names || teacher.name || "");
      form.append("teacherInitials", (teacher.names || "").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase());
      replyFiles.forEach(f => form.append("attachments", f));
      await axios.post(`/api/support-desk/tickets/${ticket.id}/teacher-comment`, form, {
        headers: { "Content-Type": "multipart/form-data", ...authHeaders },
      });
      setReplyText("");
      setReplyFiles([]);
      await refresh();
    } catch {}
    setSending(false);
  };

  const addReplyFiles = (e) => {
    const added = Array.from(e.target.files || []);
    setReplyFiles(fs => [...fs, ...added].slice(0, 5));
    e.target.value = "";
  };

  const reopen = async () => {
    setReopening(true);
    try {
      const { data } = await axios.post(`/api/support-desk/tickets/${ticket.id}/reopen`, {
        teacherNid: (teacher.nationalId || teacher.nid || "").replace(/\s/g, ""),
        teacherName: teacher.names || teacher.name || "",
      }, { headers: authHeaders });
      setTicket(data);
    } catch {}
    setReopening(false);
  };

  const cat = catByKey(categories, ticket.categoryKey);
  const prio = PRIORITIES.find(p => p.key === ticket.priorityKey) || PRIORITIES[1];
  const timeline = ticket.timeline || [];
  const comments = (ticket.comments || []).filter(c => !c.isInternal);
  const attachments = ticket.attachments || [];
  const flowDoneIdx = FLOW.indexOf(ticket.status);

  return (
    <Box sx={{ minHeight: "100vh", background: T.bg }}>
      <TopBar teacher={teacher} />
      <Box sx={{ maxWidth: 740, mx: "auto", px: 2, pt: 3, pb: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SdBtn variant="soft" onClick={onBack} startIcon={<ArrowBackIcon fontSize="small" />}>Back</SdBtn>
          {loadingTicket && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>

        {/* Header */}
        <SdCard style={{ mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: cat.color + "22", color: cat.color, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
              {cat.name.slice(0, 2).toUpperCase()}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 17, color: T.ink }}>{ticket.subject}</Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: .5 }}>
                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: T.ink3 }}>{ticket.ref}</Typography>
                <Typography sx={{ fontSize: 12, color: T.ink2 }}>{cat.name}</Typography>
                <span style={{ color: prio.color, fontSize: 12, fontWeight: 700 }}>{prio.label} priority</span>
              </Box>
            </Box>
            <StatusPill status={ticket.status} />
          </Box>
        </SdCard>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 300px" }, gap: 2 }}>
          {/* Main column */}
          <Box>
            {/* Status banner */}
            {(() => {
              const b = STATUS_BANNER[ticket.status];
              if (!b) return null;
              const BannerIcon = b.Icon;
              return (
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", background: b.bg, borderRadius: 2.5, p: 2, mb: 2 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: "12px", background: b.color + "22", color: b.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <BannerIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 15, color: b.color, lineHeight: 1.3 }}>{b.headline}</Typography>
                    <Typography sx={{ fontSize: 13, color: b.color, opacity: .75, mt: .5, lineHeight: 1.5 }}>{b.sub}</Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* Original request */}
            <SdCard style={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5, color: T.ink }}>Your request</Typography>
              <Typography sx={{ fontSize: 14, color: T.ink, whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{ticket.body}</Typography>
              {attachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink2, mb: 1 }}>Attachments</Typography>
                  {attachments.map(a => (
                    <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, background: "#f9fafb", borderRadius: 1, mb: .5 }}>
                      <AttachFileIcon sx={{ fontSize: 16, color: T.orange }} />
                      <Typography sx={{ fontSize: 13 }}>{a.originalName}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </SdCard>

            {/* Thread */}
            <SdCard style={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 2, color: T.ink }}>Conversation with support</Typography>
              {comments.length === 0 && (
                <Typography sx={{ color: T.ink3, fontSize: 13 }}>No messages yet — support will respond shortly.</Typography>
              )}
              {comments.map(msg => {
                const isTeacher = msg.fromType === "teacher";
                return (
                  <Box key={msg.id} sx={{ display: "flex", gap: 1.5, mb: 2.5, flexDirection: isTeacher ? "row-reverse" : "row" }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: "50%", background: isTeacher ? T.orangeGrad : "linear-gradient(135deg,#2563eb,#1e3a8a)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12, flexShrink: 0 }}>
                      {(msg.authorInitials || msg.authorName?.slice(0,2) || "??").toUpperCase()}
                    </Box>
                    <Box sx={{ maxWidth: "78%" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3, mb: .5, textAlign: isTeacher ? "right" : "left" }}>{msg.authorName}</Typography>
                      <Box sx={{
                        background: isTeacher ? T.navy : "#fff",
                        border: isTeacher ? "none" : `1px solid ${T.line}`,
                        borderRadius: isTeacher ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                        p: "10px 14px",
                      }}>
                        {msg.body && <Typography sx={{ fontSize: 14, color: isTeacher ? "#fff" : T.ink, lineHeight: 1.6 }}>{msg.body}</Typography>}
                        {(msg.attachments || []).map(a => {
                          const url = `/uploads/support-desk/${a.storedName}`;
                          const c = isTeacher ? "rgba(255,255,255,.92)" : T.ink2;
                          return (
                            <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: .75, mt: .75, fontSize: 12.5, color: c }}>
                              <AttachFileIcon sx={{ fontSize: 15 }} />
                              <span style={{ flexShrink: 1, minWidth: 0, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.originalName}</span>
                              <a href={url} target="_blank" rel="noopener noreferrer" title="Preview" style={{ color: "inherit", display: "inline-flex", alignItems: "center" }}>
                                <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                              </a>
                              <a href={url} download={a.originalName} title="Download" style={{ color: "inherit", display: "inline-flex", alignItems: "center" }}>
                                <FileDownloadOutlinedIcon sx={{ fontSize: 17 }} />
                              </a>
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography sx={{ fontSize: 11, color: T.ink3, mt: .5, textAlign: isTeacher ? "right" : "left" }}><TimeAgo iso={msg.createdAt} /></Typography>
                    </Box>
                  </Box>
                );
              })}

              {ticket.status !== "resolved" && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ border: `1.5px solid ${T.line}`, borderRadius: 2.5, overflow: "hidden", "&:focus-within": { borderColor: T.orange, boxShadow: `0 0 0 3px ${T.orange}20` }, transition: "border-color .15s, box-shadow .15s" }}>
                    <TextField
                      fullWidth multiline rows={3} size="small"
                      placeholder="Reply to the support team…"
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
                      variant="standard"
                      InputProps={{ disableUnderline: true, sx: { px: 1.5, pt: 1.5, pb: .5, fontSize: 14 } }}
                    />
                    {replyFiles.length > 0 && (
                      <Box sx={{ px: 1.5, pb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {replyFiles.map((f, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: .5, background: "#eef2f8", borderRadius: 999, px: 1.25, py: .25, fontSize: 12 }}>
                            <AttachFileIcon sx={{ fontSize: 14, color: T.orange }} />
                            <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                            <span onClick={() => setReplyFiles(fs => fs.filter((_, j) => j !== i))} style={{ cursor: "pointer", color: "#ef4444", fontWeight: 700 }}>×</span>
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, borderTop: `1px solid ${T.line}` }}>
                      <Box component="span" onClick={() => replyFileRef.current?.click()} sx={{ display: "inline-flex", alignItems: "center", gap: .5, color: T.ink3, fontSize: 13, fontWeight: 600, cursor: "pointer", "&:hover": { color: T.orange }, transition: "color .15s" }}>
                        <AttachFileIcon sx={{ fontSize: 17 }} /> Attach
                      </Box>
                      <input ref={replyFileRef} type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx" onChange={addReplyFiles} />
                      <SdBtn onClick={sendReply} disabled={sending || (!replyText.trim() && !replyFiles.length)}>
                        {sending ? <><CircularProgress size={13} sx={{ color: "#fff" }} /> Sending…</> : <>Send reply <SendIcon sx={{ fontSize: 15, ml: .25 }} /></>}
                      </SdBtn>
                    </Box>
                  </Box>
                </Box>
              )}

              {ticket.status === "resolved" && (
                <Box sx={{ mt: 2, p: 2, background: "#E3F5EC", borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#12885A" }}>Resolved & closed</Typography>
                    <Typography sx={{ fontSize: 13, color: T.ink2 }}>If your issue isn't fully resolved, you can reopen this request within 14 days.</Typography>
                  </Box>
                  <SdBtn variant="outline" onClick={reopen} disabled={reopening} startIcon={<RefreshIcon fontSize="small" />}>
                    {reopening ? "Reopening…" : "Reopen"}
                  </SdBtn>
                </Box>
              )}
            </SdCard>
          </Box>

          {/* Sidebar */}
          <Box>
            <SdCard style={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 2, color: T.ink, fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em" }}>Progress</Typography>
              {FLOW.map((s, i) => {
                const isDone   = i < flowDoneIdx || (ticket.status === "resolved" && i <= flowDoneIdx);
                const isActive = i === flowDoneIdx && ticket.status !== "resolved";
                const isPending = i > flowDoneIdx;
                const tEvent = timeline.find(e => e.status === s);
                return (
                  <Box key={s} sx={{ display: "flex", gap: 1.5, mb: 1.5, opacity: isPending ? .45 : 1 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: "50%",
                        background: isDone || (ticket.status === "resolved" && i <= flowDoneIdx) ? "#12885A" : isActive ? T.orange : T.line,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {(isDone || ticket.status === "resolved") && <CheckCircleIcon sx={{ color: "#fff", fontSize: 14 }} />}
                      </Box>
                      {i < FLOW.length - 1 && <Box sx={{ width: 2, flexGrow: 1, minHeight: 16, background: isDone ? "#12885A" : T.line, mt: .5 }} />}
                    </Box>
                    <Box sx={{ pb: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: isActive ? T.orange : T.ink }}>
                        {STATUS_MAP[s]?.label || s}
                        {isPending && <span style={{ color: T.ink3, fontWeight: 400 }}> — upcoming</span>}
                      </Typography>
                      {tEvent && <Typography sx={{ fontSize: 12, color: T.ink3 }}>{tEvent.actorName} · <TimeAgo iso={tEvent.createdAt} /></Typography>}
                      {tEvent?.note && <Typography sx={{ fontSize: 12, color: T.ink2, mt: .25 }}>{tEvent.note}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </SdCard>

            <SdCard>
              <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em", color: T.ink }}>Details</Typography>
              {[
                ["Raised",   ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "—"],
                ["School",   ticket.teacherSchool || "—"],
                ["District", ticket.teacherDistrict || "—"],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                  <Typography sx={{ fontSize: 12, color: T.ink3, minWidth: 70 }}>{k}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 12, color: T.ink2 }}>For urgent help, call the TMIS support line.</Typography>
            </SdCard>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Shared top bar ────────────────────────────────────────────────────────────

function TopBar({ teacher }) {
  const initials = teacher ? (teacher.names || "").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase() : "";
  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 10, background: T.navy, px: { xs: 1.5, sm: 3 }, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
      <HelpOutlineIcon sx={{ color: T.orange, fontSize: 22, flexShrink: 0 }} />
      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 16, flexShrink: 0 }}>TMIS <em style={{ color: T.orange }}>Support</em></Typography>
      <Box sx={{ flexGrow: 1 }} />
      {teacher && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography sx={{ color: "#aab4c8", fontSize: 13, display: { xs: "none", sm: "block" }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{teacher.names || teacher.name}</Typography>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", background: T.orangeGrad, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12, flexShrink: 0 }}>{initials}</Box>
        </Box>
      )}
    </Box>
  );
}

// ── Root state machine ────────────────────────────────────────────────────────

export default function SupportDesk() {
  const [view, setView]         = useState("entry");   // entry|home|new|confirm|track
  const [teacher, setTeacher]   = useState(null);
  const [tickets, setTickets]   = useState([]);
  const [selTicket, setSelTicket] = useState(null);
  const [lastTicket, setLastTicket] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [accessToken, setAccessToken] = useState(null); // set after OTP; used to authorize teacher API calls

  // Header sent on teacher API calls; empty when OTP gating is off.
  const authHeaders = accessToken ? { "x-sd-access": accessToken } : {};

  useEffect(() => {
    axios.get("/api/support-desk/categories")
      .then(({ data }) => { if (data && data.length) setCategories(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!document.getElementById("sd-jakarta-font")) {
      const link = document.createElement("link");
      link.id = "sd-jakarta-font";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const loadTickets = async (t, token) => {
    const nid = (t.nationalId || t.nid || "").replace(/\s/g, "");
    if (!nid) return;
    setLoading(true);
    try {
      const { data } = await axios.get("/api/support-desk/tickets/mine", {
        params: { nid },
        headers: token ? { "x-sd-access": token } : {},
      });
      setTickets(data);
    } catch {}
    setLoading(false);
  };

  const handleVerified = (citizen, token) => {
    setTeacher(citizen);
    setAccessToken(token || null);
    loadTickets(citizen, token);
    setView("home");
  };

  const handleSubmit = (ticket) => {
    setLastTicket(ticket);
    setTickets(ts => [ticket, ...ts]);
    setView("confirm");
  };

  const screen =
    view === "entry" ? (
      <TeacherEntry onVerified={handleVerified} />
    ) : view === "home" ? (
      <TeacherHome
        teacher={teacher} tickets={tickets} loading={loading} categories={categories}
        onNew={() => setView("new")}
        onOpen={(t) => { setSelTicket(t); setView("track"); }}
      />
    ) : view === "new" ? (
      <NewRequest teacher={teacher} categories={categories} authHeaders={authHeaders} onCancel={() => setView("home")} onSubmit={handleSubmit} />
    ) : view === "confirm" && lastTicket ? (
      <Confirmation
        ticket={lastTicket} teacher={teacher}
        onHome={() => setView("home")}
        onTrack={() => { setSelTicket(lastTicket); setView("track"); }}
      />
    ) : view === "track" && selTicket ? (
      <TrackDetail ticket={selTicket} teacher={teacher} categories={categories} authHeaders={authHeaders} onBack={() => setView("home")} />
    ) : null;

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {screen}
    </Box>
  );
}
